// Is this merchant actually ready to ship?
//
// Every field read here already exists on the merchant document and is already
// returned by merchant/searchCached when named in the `includes` projection --
// so this is presentation over data we were already fetching and throwing away.
// No new endpoint, no backend change.
//
// Answering "which merchants aren't live yet, and why" previously meant opening
// each merchant and clicking through tabs.

export const READINESS_FIELDS = [
    'status', 'kycStatus', 'tatStatus', 'rateCardVersion',
    'allocationVersion', 'commercialConstructConfirmed',
];

const KYC = {
    KYC_APPROVED: { tone: 'done',    label: 'KYC',  hint: 'KYC approved' },
    KYC_UPLOADED: { tone: 'pending', label: 'KYC',  hint: 'Documents uploaded, awaiting review' },
    KYC_PENDING:  { tone: 'pending', label: 'KYC',  hint: 'KYC not started' },
    KYC_REJECTED: { tone: 'blocked', label: 'KYC',  hint: 'Documents rejected — merchant cannot go live' },
};

/**
 * @param  m  merchant document (or a search row carrying the fields above)
 * @return array of { key, tone, label, hint } -- ready to render as chips
 */
export function merchantReadiness(m) {
    if (!m) return [];

    const out = [];

    const kycKey = m.kycStatus || m.status;
    out.push({
        key: 'kyc',
        ...(KYC[kycKey] || { tone: 'none', label: 'KYC', hint: 'KYC state unknown' }),
    });

    out.push({
        key: 'pricing',
        tone: m.rateCardVersion ? 'done' : 'pending',
        label: 'Pricing',
        hint: m.rateCardVersion
            ? `Rate card v${m.rateCardVersion} published`
            : 'No rate card published — orders will fall back to default pricing',
    });

    const tat = (m.tatStatus || '').toLowerCase();
    out.push({
        key: 'tat',
        tone: tat === 'published' ? 'done' : tat === 'pending' || !tat ? 'pending' : 'none',
        label: 'TAT',
        hint: tat === 'published' ? 'TAT published' : 'TAT not published yet',
    });

    out.push({
        key: 'allocation',
        tone: m.allocationVersion ? 'done' : 'pending',
        label: 'Allocation',
        hint: m.allocationVersion
            ? `Allocation set ${m.allocationVersion} published`
            : 'No allocation published — courier choice falls back to platform rules',
    });

    return out;
}

/**
 * One-line summary for a list row: "3 of 4 ready".
 */
export function readinessSummary(m) {
    const r = merchantReadiness(m);
    if (!r.length) return null;
    const done = r.filter((x) => x.tone === 'done').length;
    const blocked = r.some((x) => x.tone === 'blocked');
    return { done, total: r.length, blocked, complete: done === r.length };
}
